import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Camera,
  CheckCircle2,
  Loader2,
  MapPin,
  X,
} from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { useUser } from '@/shared/hooks/useUser';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { handleApiError } from '@/shared/api/requests';
import { completeUnitTourItem, getUnitTour } from './api';
import type { UnitTourData, UnitTourItem } from './types';
import UnitTourMiniMap from './UnitTourMiniMap';

// Pannellum is heavy and is a CommonJS module. Loading it eagerly on this
// public route pulls it into the first-navigation critical path, which can
// stall the very first QR-scan load. Code-split it so the page shell, tour
// fetch, and company picker render immediately; pannellum loads only when a
// panorama is actually shown.
const UnitTourPanorama = lazy(() => import('./UnitTourPanorama'));

const STATUS_LABELS: Record<string, string> = {
  OPEN: 'Open',
  PENDING_APPROVAL: 'Pending Approval',
  COMPLETED: 'Completed',
  OVERDUE: 'Overdue',
  CLOSED: 'Closed',
};

const STATUS_BADGE: Record<string, string> = {
  OPEN: 'bg-blue-100 text-blue-800',
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  COMPLETED: 'bg-green-100 text-green-800',
  OVERDUE: 'bg-red-100 text-red-800',
  CLOSED: 'bg-gray-100 text-gray-700',
};

const isSubmittable = (status: string) =>
  status === 'OPEN' || status === 'OVERDUE';

const userFullName = (user: { first_name: string; last_name: string } | null | undefined) => {
  if (!user) return '';
  return `${user.first_name} ${user.last_name}`.trim();
};

const UnitTourPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { user, loading: userLoading } = useUser();
  const [data, setData] = useState<UnitTourData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [stepIndex, setStepIndex] = useState(0);

  const [activeItem, setActiveItem] = useState<UnitTourItem | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [certifierName, setCertifierName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isAuthenticated = Boolean(user);
  const requiresCertifierName = !userLoading && !isAuthenticated;

  const loadTour = useCallback(async () => {
    if (!token) {
      setError('Invalid QR Punch Walk link.');
      setLoading(false);
      return;
    }
    try {
      const tour = await getUnitTour(token);
      setData(tour);
      if (tour.companies.length === 1) {
        setSelectedCompanyId(tour.companies[0].id);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void loadTour();
  }, [loadTour]);

  // Stops are the company's punch items pinned to a hotspot in the unit, so the
  // tour can walk to each one. (Location-only items still appear in the report.)
  const stops = useMemo(() => {
    if (!data || selectedCompanyId == null) return [] as UnitTourItem[];
    return data.items.filter(
      (item) =>
        item.company_id === selectedCompanyId &&
        item.hotspot_id != null &&
        item.photo_id != null,
    );
  }, [data, selectedCompanyId]);

  const currentStop = stops[stepIndex] ?? null;

  const currentPhoto = useMemo(() => {
    if (!data || !currentStop) return null;
    return data.photos.find((photo) => photo.id === currentStop.photo_id) ?? null;
  }, [data, currentStop]);

  const goToStop = useCallback(
    (index: number) => {
      if (index < 0 || index >= stops.length) return;
      setStepIndex(index);
      setActiveItem(null);
    },
    [stops.length],
  );

  const handleHotspotClick = useCallback(
    (hotspotId: number) => {
      const item = stops.find((stop) => stop.hotspot_id === hotspotId);
      if (item) setActiveItem(item);
    },
    [stops],
  );

  const handleSelectFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files ? Array.from(event.target.files) : [];
    setFiles((current) => [...current, ...selected]);
    event.target.value = '';
  };

  const removeFile = (index: number) => {
    setFiles((current) => current.filter((_, i) => i !== index));
  };

  const closeCompletion = () => {
    setActiveItem(null);
    setFiles([]);
    setCertifierName('');
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    if (!token || !activeItem || files.length === 0) return;
    const trimmedName = certifierName.trim();
    if (requiresCertifierName && !trimmedName) {
      setSubmitError('Type your name to certify this item is complete.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await completeUnitTourItem(
        token,
        activeItem.id,
        selectedCompanyId,
        files,
        requiresCertifierName ? trimmedName : undefined,
      );
      closeCompletion();
      await loadTour();
    } catch (err) {
      setSubmitError(handleApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading QR Punch Walk...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <h1 className="mb-3 text-2xl font-bold">Unable to load QR Punch Walk</h1>
          <p className="text-muted-foreground">{error ?? 'This QR Punch Walk is not available.'}</p>
        </div>
      </div>
    );
  }

  // Company picker (blocks the tour until a company is chosen).
  const showCompanyPicker = selectedCompanyId == null;

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-black">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 bg-background px-4 py-3 shadow">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {data.project.name}
          </p>
          <h1 className="truncate text-lg font-bold">
            {data.unit.layout_name ? `${data.unit.layout_name} - ${data.unit.name}` : data.unit.name}
          </h1>
        </div>
        {selectedCompanyId != null && (
          <Badge variant="secondary" className="flex-shrink-0">
            {data.companies.find((c) => c.id === selectedCompanyId)?.name ?? 'Company'}
          </Badge>
        )}
      </div>

      {/* Panorama */}
      <div className="relative min-h-0 flex-1">
        {currentPhoto ? (
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center bg-black">
                <Loader2 className="h-10 w-10 animate-spin text-white/80" />
              </div>
            }
          >
            <UnitTourPanorama
              sceneKey={currentPhoto.id}
              imageUrl={currentPhoto.signed_url}
              hotspots={currentPhoto.hotspots}
              target={
                currentStop && currentStop.pitch != null && currentStop.yaw != null
                  ? { pitch: currentStop.pitch, yaw: currentStop.yaw }
                  : null
              }
              onHotspotClick={handleHotspotClick}
            />
          </Suspense>
        ) : (
          <div className="flex h-full items-center justify-center text-white">
            {stops.length === 0
              ? 'No punch items are assigned to your company in this unit.'
              : 'Select a stop to begin.'}
          </div>
        )}

        {data.map && selectedCompanyId != null && currentPhoto && (
          <UnitTourMiniMap map={data.map} currentPhotoId={currentStop?.photo_id ?? null} />
        )}

        {/* Current item callout */}
        {currentStop && !activeItem && (
          <button
            type="button"
            onClick={() => setActiveItem(currentStop)}
            className="absolute bottom-24 left-1/2 w-[90%] max-w-md -translate-x-1/2 rounded-lg bg-background/95 p-3 text-left shadow-lg backdrop-blur"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-sm font-semibold">
                <MapPin className="h-4 w-4 text-primary" />
                {currentStop.title}
              </span>
              <Badge className={STATUS_BADGE[currentStop.status] ?? ''}>
                {STATUS_LABELS[currentStop.status] ?? currentStop.status}
              </Badge>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Tap to complete this item</p>
          </button>
        )}
      </div>

      {/* Tour navigation */}
      {stops.length > 0 && (
        <div className="flex items-center justify-between gap-3 bg-background px-4 py-3 shadow-inner">
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToStop(stepIndex - 1)}
            disabled={stepIndex === 0}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Prev
          </Button>
          <span className="text-sm font-medium text-muted-foreground">
            Stop {stepIndex + 1} of {stops.length}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToStop(stepIndex + 1)}
            disabled={stepIndex >= stops.length - 1}
          >
            Next
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Company picker dialog */}
      <Dialog open={showCompanyPicker}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Select your company</DialogTitle>
            <DialogDescription>
              Choose the company you work for to see the punch items assigned to you in this unit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {data.companies.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                There are no punch items assigned to a company in this unit yet.
              </p>
            ) : (
              data.companies.map((company) => (
                <Button
                  key={company.id}
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    setSelectedCompanyId(company.id);
                    setStepIndex(0);
                  }}
                >
                  {company.name}
                </Button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Completion panel */}
      <Dialog open={activeItem !== null} onOpenChange={(open) => { if (!open) closeCompletion(); }}>
        <DialogContent className="max-w-md">
          {activeItem && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {activeItem.title}
                </DialogTitle>
                <DialogDescription>
                  {activeItem.description || 'Complete this punch item and submit proof photos.'}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <Badge className={STATUS_BADGE[activeItem.status] ?? ''}>
                  {STATUS_LABELS[activeItem.status] ?? activeItem.status}
                </Badge>

                {activeItem.proof_attachments.length > 0 && (
                  <div>
                    <p className="mb-1 text-xs font-medium text-muted-foreground">
                      Submitted proof
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {activeItem.proof_attachments.map((attachment) =>
                        attachment.file_url ? (
                          <a
                            key={attachment.id}
                            href={attachment.file_url}
                            target="_blank"
                            rel="noreferrer"
                            className="h-16 w-16 overflow-hidden rounded border"
                          >
                            <img
                              src={attachment.file_url}
                              alt="Proof"
                              className="h-full w-full object-cover"
                            />
                          </a>
                        ) : null,
                      )}
                    </div>
                  </div>
                )}

                {(activeItem.completed_by || activeItem.completion_certifier_name) && (
                  <div className="rounded-[13px] border border-border bg-muted px-3.5 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                      Certified by
                    </p>
                    <p className="mt-1 text-sm font-medium">
                      {activeItem.completed_by
                        ? userFullName(activeItem.completed_by) || activeItem.completed_by.email
                        : activeItem.completion_certifier_name}
                    </p>
                  </div>
                )}

                {isSubmittable(activeItem.status) ? (
                  <>
                    {requiresCertifierName && (
                      <div className="space-y-2">
                        <Label htmlFor="completion-certifier-name">Your name</Label>
                        <Input
                          id="completion-certifier-name"
                          value={certifierName}
                          maxLength={200}
                          onChange={(event) => setCertifierName(event.target.value)}
                          placeholder="Type your full name"
                          autoComplete="name"
                        />
                      </div>
                    )}

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      multiple
                      className="hidden"
                      onChange={handleSelectFiles}
                    />
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Camera className="mr-2 h-4 w-4" />
                      Take / add photo
                    </Button>

                    {files.length > 0 && (
                      <ul className="space-y-1">
                        {files.map((file, index) => (
                          <li
                            key={`${file.name}-${index}`}
                            className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-sm"
                          >
                            <span className="truncate">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="text-muted-foreground hover:text-destructive"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {submitError && (
                      <p className="text-sm text-destructive">{submitError}</p>
                    )}

                    <Button
                      className="w-full"
                      onClick={handleSubmit}
                      disabled={
                        submitting
                        || userLoading
                        || files.length === 0
                        || (requiresCertifierName && certifierName.trim().length === 0)
                      }
                    >
                      {submitting ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                      )}
                      Submit for approval
                    </Button>
                  </>
                ) : (
                  <p className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    This item has already been submitted and is awaiting approval.
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnitTourPage;
